import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTenant } from '../../contexts/TenantContext';
import dailyReportingAPI from '../../services/dailyReportingAPI';
import reportsAPI from '../../services/reportsAPI';
import SchoolAnalytics from './SchoolAnalytics';
import TodaysAttendanceTable from './TodaysAttendanceTable';

export default function SchoolOverviewDashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const { isLoading: tenantLoading, currentSchool } = useTenant();
  
  // State management
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isViewingCustomReport, setIsViewingCustomReport] = useState(false);
  const [currentReportDateRange, setCurrentReportDateRange] = useState(null);

  // Report generation state
  const [cooldownStatus, setCooldownStatus] = useState(null);
  const [completedReports, setCompletedReports] = useState([]);
  const [pendingReports, setPendingReports] = useState([]);
  const [manualReportLoading, setManualReportLoading] = useState(false);
  const [showManualReportModal, setShowManualReportModal] = useState(false);
  const [manualReportDates, setManualReportDates] = useState({ startDate: '', endDate: '' });
  const [showAllReportsModal, setShowAllReportsModal] = useState(false);

  console.log('SchoolOverviewDashboard render - school:', currentSchool?.name, 'loading:', loading, 'tenantLoading:', tenantLoading);

  // Get today's date range (single day) - using school timezone
  const getTodayDateRange = useCallback(() => {
    const schoolTimezone = currentSchool?.timezone || 'UTC';
    
    try {
      // Get current date in school's timezone
      const now = new Date();
      const todayStr = now.toLocaleDateString('en-CA', {
        timeZone: schoolTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      
      console.log('getTodayDateRange - Using school timezone:', schoolTimezone, 'today:', todayStr);
      
      return {
        startDate: todayStr,
        endDate: todayStr
      };
    } catch (error) {
      console.warn('Error getting date in school timezone:', error);
      // Fallback to local date
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;
      
      return {
        startDate: todayStr,
        endDate: todayStr
      };
    }
  }, [currentSchool?.timezone]);

  // Get today's date string in school timezone for date input max values
  const getTodayInSchoolTimezone = useCallback(() => {
    const { startDate } = getTodayDateRange();
    return startDate;
  }, [getTodayDateRange]);

  // Load report configuration and automatic report on school change
  useEffect(() => {
    const loadReportData = async () => {
      if (!currentSchool?.id) return;

      try {
        const authToken = await getAccessTokenSilently();
        const schoolId = currentSchool?.unique_id || currentSchool?.unique_id || currentSchool?.id;
        
        // Automatic reports are disabled

        // Load cooldown status
        const cooldownResponse = await reportsAPI.checkCooldown(authToken, schoolId);
        setCooldownStatus(cooldownResponse.cooldown);

        // Load completed and pending reports
        const reportsData = await reportsAPI.getAllReports(authToken, schoolId, 10);
        setCompletedReports(reportsData.completed);
        setPendingReports(reportsData.pending);

      } catch (err) {
        console.error('Error loading report data:', err);
        // Don't set error for report loading failures as they're supplementary
      }
    };

    loadReportData();
  }, [currentSchool, getAccessTokenSilently]);

  // Refresh reports every 30 seconds to check for completed pending reports
  useEffect(() => {
    if (!currentSchool?.id || pendingReports.length === 0) return;

    const refreshReports = async () => {
      try {
        const authToken = await getAccessTokenSilently();
        const schoolId = currentSchool?.unique_id || currentSchool?.unique_id || currentSchool?.id;
        
        const reportsData = await reportsAPI.getAllReports(authToken, schoolId, 10);
        setCompletedReports(reportsData.completed);
        setPendingReports(reportsData.pending);
      } catch (err) {
        console.error('Error refreshing reports:', err);
      }
    };

    const interval = setInterval(refreshReports, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [currentSchool, getAccessTokenSilently, pendingReports.length]);

  // Handle manual report generation
  const handleManualReportGeneration = async () => {
    if (!currentSchool?.id || !manualReportDates.startDate || !manualReportDates.endDate) {
      return;
    }

    try {
      setManualReportLoading(true);
      const authToken = await getAccessTokenSilently();
      const schoolId = currentSchool?.unique_id || currentSchool?.unique_id || currentSchool?.id;

      const response = await reportsAPI.requestManualReport(
        authToken,
        schoolId,
        manualReportDates.startDate,
        manualReportDates.endDate
      );

      // Close modal and refresh data
      setShowManualReportModal(false);
      setManualReportDates({ startDate: '', endDate: '' });

      // Refresh cooldown status and reports
      const cooldownResponse = await reportsAPI.checkCooldown(authToken, schoolId);
      setCooldownStatus(cooldownResponse.cooldown);

      // Refresh reports to show the new pending request
      const reportsData = await reportsAPI.getAllReports(authToken, schoolId, 10);
      setCompletedReports(reportsData.completed);
      setPendingReports(reportsData.pending);

      console.log('Manual report request submitted successfully:', response);

    } catch (err) {
      console.error('Error generating manual report:', err);
      setError(`Failed to generate report: ${err.message}`);
    } finally {
      setManualReportLoading(false);
    }
  };

  // Load attendance data when school changes (today's data only)
  useEffect(() => {
    const loadAttendanceData = async () => {
      if (!currentSchool?.id) {
        console.log('No current school available');
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setIsViewingCustomReport(false); // Reset to today's view
        setCurrentReportDateRange(null); // Clear report date range

        const { startDate } = getTodayDateRange();
        console.log('Loading school attendance analytics for school:', currentSchool.id, 'Date:', startDate);

        // Get Auth0 token
        const authToken = await getAccessTokenSilently();

        // Get the school ID for the API call
        const schoolId = currentSchool?.unique_id || currentSchool?.unique_id || currentSchool?.id;
        if (!schoolId) {
          throw new Error('School ID not available');
        }
        
        // Use daily reporting service for today's data (faster and doesn't block heavy calculations)
        const response = await dailyReportingAPI.getDailySchoolAnalytics(authToken, schoolId, startDate);

        console.log('School attendance analytics loaded:', response);
        setAttendanceData(response);

      } catch (err) {
        console.error('Error loading school attendance analytics:', err);
        setError('Failed to load school attendance analytics. Please try again.');
        setAttendanceData(null);
      } finally {
        setLoading(false);
      }
    };

    loadAttendanceData();
  }, [currentSchool, getTodayDateRange, getAccessTokenSilently]);

  // Function to handle going back to today's view
  const handleBackToToday = async () => {
    if (!currentSchool?.id) return;

    try {
      setLoading(true);
      setError(null);
      setIsViewingCustomReport(false);
      setCurrentReportDateRange(null);

      const { startDate } = getTodayDateRange();
      const authToken = await getAccessTokenSilently();
      const schoolId = currentSchool?.unique_id || currentSchool?.unique_id || currentSchool?.id;
      
      const response = await dailyReportingAPI.getDailySchoolAnalytics(authToken, schoolId, startDate);
      setAttendanceData(response);
    } catch (err) {
      console.error('Error loading today\'s data:', err);
      setError('Failed to load today\'s attendance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading if tenant context is still loading
  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (loading && !attendanceData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading school overview data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isViewingCustomReport ? 'Custom Attendance Report' : "Today's Attendance Overview"}
        </h1>
        
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {isViewingCustomReport ? (
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleBackToToday}
                  className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  <span className="text-sm font-medium">Back to Today</span>
                </button>
                <p className="text-gray-600">
                  Viewing historical attendance data for {currentSchool?.name || 'this school'}.
                </p>
              </div>
            ) : (
              <p className="text-gray-600">
                Monitor today's attendance rates across all classes in {currentSchool?.name || 'this school'}.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Combined Report Generation and Reports Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-2">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* Left Side - Report Generation */}
          <div className="lg:w-1/3 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Generate Report</h2>
              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                Up to 180 Days
              </span>
            </div>

            {cooldownStatus && !cooldownStatus.canGenerate ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">⏳ Report generation on cooldown</p>
                <p className="text-xs text-yellow-600 mt-1">Try again in {cooldownStatus.minutesRemaining} minutes</p>
              </div>
            ) : (
              <button
                onClick={() => setShowManualReportModal(true)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                disabled={manualReportLoading}
              >
                {manualReportLoading ? 'Submitting Request...' : 'Request Custom Report'}
              </button>
            )}
          </div>

          {/* Right Side - Reports List */}
          <div className="lg:w-2/3">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Generated Reports</h2>
              {(completedReports.length > 1 || pendingReports.length > 1) && (
                <button
                  onClick={() => setShowAllReportsModal(true)}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  View All ({completedReports.length + pendingReports.length})
                </button>
              )}
            </div>
            
            <div className="space-y-3">
              {/* Show pending reports first */}
              {pendingReports.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {new Date(pendingReports[0].start_date).toLocaleDateString()} - {new Date(pendingReports[0].end_date).toLocaleDateString()}
                      </div>
                      <p className="text-xs text-gray-500">
                        Requested: {new Date(pendingReports[0].generated_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="ml-4 flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-xs text-yellow-700 font-medium">Processing</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Show latest completed report */}
              {completedReports.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {new Date(completedReports[0].start_date).toLocaleDateString()} - {new Date(completedReports[0].end_date).toLocaleDateString()}
                      </div>
                      <p className="text-xs text-gray-500">
                        Generated: {new Date(completedReports[0].generated_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        const report = completedReports[0];
                        if (report.report_data) {
                          try {
                            const reportData = typeof report.report_data === 'string' 
                              ? JSON.parse(report.report_data)
                              : report.report_data;
                            
                            const transformedData = {
                              dailyAnalytics: reportData.dailyBreakdown?.map(day => ({
                                date: day.date,
                                attendanceRate: day.attendanceRate,
                                presentCount: Array.isArray(day.presentStudents) ? day.presentStudents.length : day.presentStudents,
                                absentCount: Array.isArray(day.absentStudents) ? day.absentStudents.length : day.absentStudents,
                                lateCount: Array.isArray(day.lateStudents) ? day.lateStudents.length : (day.lateStudents || 0),
                                excusedCount: Array.isArray(day.excusedStudents) ? day.excusedStudents.length : (day.excusedStudents || 0),
                                totalRecords: day.totalStudents,
                                uniqueStudentCount: day.totalStudents,
                                // Include student details for display
                                presentStudents: Array.isArray(day.presentStudents) ? day.presentStudents : [],
                                absentStudents: Array.isArray(day.absentStudents) ? day.absentStudents : [],
                                lateStudents: Array.isArray(day.lateStudents) ? day.lateStudents : [],
                                excusedStudents: Array.isArray(day.excusedStudents) ? day.excusedStudents : []
                              })) || [],
                              classStatistics: reportData.classStatistics || []
                            };
                            
                            setAttendanceData(transformedData);
                            setIsViewingCustomReport(true);
                            setCurrentReportDateRange({
                              startDate: report.start_date,
                              endDate: report.end_date
                            });
                          } catch (error) {
                            console.error('Error parsing report data:', error);
                          }
                        }
                      }}
                      className="ml-4 px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex-shrink-0"
                    >
                      View
                    </button>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {completedReports.length === 0 && pendingReports.length === 0 && (
                <div className="text-center py-6 text-gray-500">
                  <p className="text-sm">No reports yet. Generate your first custom report to get started.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Manual Report Modal */}
      {showManualReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate Custom Report</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={manualReportDates.startDate}
                  onChange={(e) => setManualReportDates(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  max={getTodayInSchoolTimezone()}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={manualReportDates.endDate}
                  onChange={(e) => setManualReportDates(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min={manualReportDates.startDate}
                  max={getTodayInSchoolTimezone()}
                />
              </div>

              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  📊 Reports can cover up to 180 days
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  There's a 30-minute cooldown between manual reports
                </p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowManualReportModal(false);
                  setManualReportDates({ startDate: '', endDate: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={manualReportLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleManualReportGeneration}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                disabled={manualReportLoading || !manualReportDates.startDate || !manualReportDates.endDate}
              >
                {manualReportLoading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Reports Modal */}
      {showAllReportsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">All Reports</h3>
              <button
                onClick={() => setShowAllReportsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-6">
                {/* Completed Reports */}
                {completedReports.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                      Completed Reports ({completedReports.length})
                    </h4>
                    <div className="space-y-3">
                      {completedReports.map((report) => (
                        <div key={report.id} className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 mb-1">
                                {new Date(report.start_date).toLocaleDateString()} - {new Date(report.end_date).toLocaleDateString()}
                              </div>
                              <p className="text-xs text-gray-500">
                                Generated: {new Date(report.generated_at).toLocaleString()}
                              </p>
                            </div>
                            
                            <button
                              onClick={() => {
                                // Transform the report data to match the format expected by SchoolAnalytics
                                if (report.report_data) {
                                  try {
                                    const reportData = typeof report.report_data === 'string' 
                                      ? JSON.parse(report.report_data)
                                      : report.report_data;
                                    
                                    // Transform dailyBreakdown to dailyAnalytics format
                                    const transformedData = {
                                      dailyAnalytics: reportData.dailyBreakdown?.map(day => ({
                                        date: day.date,
                                        attendanceRate: day.attendanceRate,
                                        presentCount: Array.isArray(day.presentStudents) ? day.presentStudents.length : day.presentStudents,
                                        absentCount: Array.isArray(day.absentStudents) ? day.absentStudents.length : day.absentStudents,
                                        lateCount: Array.isArray(day.lateStudents) ? day.lateStudents.length : (day.lateStudents || 0),
                                        excusedCount: Array.isArray(day.excusedStudents) ? day.excusedStudents.length : (day.excusedStudents || 0),
                                        totalRecords: day.totalStudents,
                                        uniqueStudentCount: day.totalStudents,
                                        // Include student details for display
                                        presentStudents: Array.isArray(day.presentStudents) ? day.presentStudents : [],
                                        absentStudents: Array.isArray(day.absentStudents) ? day.absentStudents : [],
                                        lateStudents: Array.isArray(day.lateStudents) ? day.lateStudents : [],
                                        excusedStudents: Array.isArray(day.excusedStudents) ? day.excusedStudents : []
                                      })) || [],
                                      classStatistics: reportData.classStatistics || []
                                    };
                                    
                                    setAttendanceData(transformedData);
                                    setIsViewingCustomReport(true);
                                    setCurrentReportDateRange({
                                      startDate: report.start_date,
                                      endDate: report.end_date
                                    });
                                    setShowAllReportsModal(false); // Close modal after selection
                                  } catch (error) {
                                    console.error('Error parsing report data:', error);
                                  }
                                }
                              }}
                              className="ml-4 px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                            >
                              View Report
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Reports */}
                {pendingReports.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                      <span className="w-3 h-3 bg-yellow-500 rounded-full mr-2 animate-pulse"></span>
                      Pending Reports ({pendingReports.length})
                    </h4>
                    <div className="space-y-3">
                      {pendingReports.map((report) => (
                        <div key={report.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 mb-1">
                                {new Date(report.start_date).toLocaleDateString()} - {new Date(report.end_date).toLocaleDateString()}
                              </div>
                              <p className="text-xs text-gray-500">
                                Requested: {new Date(report.generated_at).toLocaleString()}
                              </p>
                            </div>
                            
                            <div className="ml-4 flex items-center space-x-2">
                              <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                              <span className="text-sm text-yellow-700 font-medium">Processing...</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {completedReports.length === 0 && pendingReports.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📊</span>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">No Reports Available</h4>
                    <p className="text-gray-600">Generate your first custom report to see it here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-8">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Analytics */}
      {attendanceData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-2">
          <SchoolAnalytics
            attendanceData={attendanceData}
            schoolData={currentSchool}
            dateRange={getTodayDateRange()}
            isViewingCustomReport={isViewingCustomReport}
            customReportDateRange={currentReportDateRange}
          />
        </div>
      )}

      {/* Today's Student Attendance Table - Only show when viewing today's data */}
      {attendanceData && !isViewingCustomReport && (
        <TodaysAttendanceTable attendanceData={attendanceData} />
      )}

      {/* Empty State */}
      {!currentSchool && !loading && (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🏫</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No School Selected</h3>
            <p className="text-gray-600 mb-6">
              Please select a school to view attendance analytics.
            </p>
          </div>
        </div>
      )}

      {/* No Data State */}
      {currentSchool && !attendanceData && !loading && !error && (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">📊</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Data Available</h3>
            <p className="text-gray-600 mb-6">
              No attendance data found for {currentSchool.name} in the selected date range.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
